import type { Person, PersonAccountLink, ProfileManagementGrant, Role, Session, UUID } from './types';
import { circleDetailsRight, circlePortraitRight, mayEditCircle } from './ownership';

/**
 * Permission rules (section 8). In production these are enforced again by
 * Row Level Security and server-side commands; the UI uses them only to show
 * or hide actions and to decide whether a change is applied or proposed.
 */
export interface Actor {
  session: Session;
  grants: ProfileManagementGrant[];
}

export const LOW_RISK_SELF_FIELDS = ['nickname', 'portraitUrl', 'notes', 'birthPlaceId'] as const;
export const IDENTITY_FIELDS = ['status', 'birth', 'death', 'gender', 'names'] as const;

export function isEditor(role: Role): boolean {
  return role === 'editor' || role === 'admin';
}

export function isAdmin(role: Role): boolean {
  return role === 'admin';
}

export function managesPerson(actor: Actor, personId: UUID): boolean {
  if (actor.session.personId === personId) return true;
  return actor.grants.some((g) => g.managedPersonId === personId && g.status === 'active' && g.helperAccountId === actor.session.accountId);
}

/** Can edit any field directly without review? */
export function canEditPersonDirectly(actor: Actor, personId: UUID): boolean {
  return isEditor(actor.session.role) || managesPerson(actor, personId);
}

/** Can open the edit form at all (either direct edit or propose a correction)? */
export function canProposePersonEdit(actor: Actor): boolean {
  return actor.session.role !== 'member' || true; // every member may suggest corrections
}

/**
 * Decide whether a specific field change is applied directly or routed to
 * review. Identity fields on other people always go to review for members
 * and helpers; editors apply directly.
 */
export function fieldChangeMode(actor: Actor, personId: UUID, field: string): 'direct' | 'review' {
  if (isEditor(actor.session.role)) return 'direct';
  if (managesPerson(actor, personId)) {
    return (IDENTITY_FIELDS as readonly string[]).includes(field) && actor.session.personId !== personId ? 'review' : 'direct';
  }
  return 'review';
}

export function relationshipChangeMode(actor: Actor): 'direct' | 'review' {
  return isEditor(actor.session.role) ? 'direct' : 'review';
}

export function canRecordFor(actor: Actor, personId: UUID): boolean {
  return managesPerson(actor, personId);
}

export function canManageFamily(actor: Actor): boolean {
  return isEditor(actor.session.role);
}

export function canSeeReviewMarkers(actor: Actor): boolean {
  return isEditor(actor.session.role);
}

export function canModerate(actor: Actor): boolean {
  return isAdmin(actor.session.role);
}

export function temporaryUserCanEditSelf(actor: Actor): boolean {
  // Section 29: open decision — prototype default is view / listen / record + low-risk self edits.
  return actor.session.kind !== 'temporary' || true;
}

/** Is this person's circle owned — i.e. actively linked to an account? */
export function personOwner(links: PersonAccountLink[], personId: UUID): UUID | null {
  return links.find((l) => l.personId === personId && l.status === 'active')?.accountId ?? null;
}

/**
 * Photos on tree circles, as offered on the person card.
 *
 * A circle belongs to whoever looks after it — the person themselves once they
 * have claimed it, otherwise the relative who added them (see
 * domain/ownership.ts). Only they may change the picture. Nobody should be able
 * to change someone else's face in the family tree, and an owned circle offers
 * no camera button at all rather than a button that fails.
 *
 * A circle nobody has taken on yet accepts its **first** photo from anyone —
 * that is how a tree of people who never joined gets its faces. Once it has
 * one, changing it is its keeper's or an administrator's decision. The whole
 * rule, and why, lives in `circlePortraitRight`.
 */
export function canChangePortrait(
  actor: Actor,
  links: PersonAccountLink[],
  grants: ProfileManagementGrant[],
  person: Pick<Person, 'id' | 'portraitUrl'>,
): boolean {
  return circlePortraitRight(actor, links, grants, person) !== 'none';
}

/**
 * Who may open Edit details for a person: anyone, unless the circle is locked —
 * a locked circle's details are changed by administrators only.
 */
export function canOpenPersonEdit(actor: Actor, person: Pick<Person, 'locked'>): boolean {
  return isAdmin(actor.session.role) || !person.locked;
}

/**
 * Whether an edit to this person is applied right away or becomes a suggestion
 * for an administrator to approve — the same rule the profile uses to decide
 * whether to offer an Edit button at all (domain/ownership.ts). Anyone the UI
 * would not have offered it to still cannot write: their change arrives as a
 * suggestion instead of a silent edit.
 */
export function personEditMode(actor: Actor, links: PersonAccountLink[], grants: ProfileManagementGrant[], person: Pick<Person, 'id' | 'locked'>): 'direct' | 'suggest' {
  return circleDetailsRight(actor, links, grants, person) === 'none' ? 'suggest' : 'direct';
}
